import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import ManageColumnsField from 'components/ManageColumnsField';
import DefaultColumnsButton from 'components/DefaultColumnsButton';
import { isEqual } from 'lodash';

import './ManageColumnsForm.styles.scss';

type FormValues = {
  hiddenColumns: string[];
  columnOrder: string[];
};

type ManageColumnsFormProps = {
  className?: string;
  initialValues: FormValues;
  headingTranslations: Record<string, string>;
  lockedColumn?: string;
  isDefaultColumns: boolean;
  isSaving?: boolean;
  saveLabel?: string;
  onSave: (hiddenColumns: string[], columnOrder: string[]) => void;
  onRestore: () => void;
  onCancel: () => void;
};

const ManageColumnsForm: React.FC<ManageColumnsFormProps> = (props) => {
  const {
    className,
    initialValues,
    headingTranslations,
    lockedColumn,
    isDefaultColumns,
    isSaving,
    saveLabel,
    onSave,
    onRestore,
    onCancel,
  } = props;

  const classes = classNames('manage-columns-form', className);

  const { t } = useTranslation();

  const onSubmit = useCallback(
    ({ hiddenColumns, columnOrder }: FormValues) =>
      onSave(hiddenColumns, columnOrder),
    [onSave],
  );

  return (
    <Form
      initialValues={initialValues}
      initialValuesEqual={isEqual}
      onSubmit={onSubmit}
      render={({ submitting, dirty, handleSubmit }) => {
        const isLoading = submitting || isSaving;

        return (
          <form onSubmit={handleSubmit} className={classes}>
            {isLoading && <Loader />}

            <ManageColumnsField
              headingTranslations={headingTranslations}
              lockedColumn={lockedColumn}
            />

            <div className="manage-columns-form__actions">
              <DefaultColumnsButton
                className="manage-columns-form__default"
                disabled={isLoading || isDefaultColumns}
                onRestore={onRestore}
              />

              <Button variant="link" onClick={onCancel}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={isLoading || !dirty}>
                {saveLabel ?? t('saveColumns')}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default ManageColumnsForm;
